import { useCallback, useEffect, useRef, useState } from "react";
import { ModalShell } from "@/components/common/ModalShell";
import { ChangeMarketAction } from "./ChangeMarketAction";
import {
  executeCurrencyMigration,
  fetchCurrencyMigrationPreview,
} from "@/api/currencyMigration";
import type {
  CurrencyMigrationBlocker,
  CurrencyMigrationItem,
  CurrencyMigrationPreview,
} from "@/types/currencyMigration";
import { formatMinorAmount } from "@/utils/entitlements";
import { fillTemplate } from "@/utils/text";
import { t, useLocalizedTexts } from "@/utils/translations";
import styles from "./CurrencySwitchDialog.module.css";

interface CurrencySwitchDialogProps {
  /** Lowercase ISO code the member's Stripe customer is pinned to, from the 409. */
  existingCurrency: string;
  /** Lowercase ISO code the cart bills in, from the 409. */
  requestedCurrency: string;
  /**
   * The member switched successfully — the caller retries the checkout session,
   * which now resolves the new customer.
   */
  onSwitched: () => void;
  /** They chose to keep paying in their existing currency, or closed the dialog. */
  onDismiss: () => void;
}

type Phase =
  | "loading"
  | "offer"
  | "switching"
  | "switched"
  | "blocked"
  | "unavailable"
  | "error";

/**
 * Offers the member the one thing that unblocks their checkout: moving their Stripe
 * billing to the currency their store sells in.
 *
 * Stripe pins a customer to one currency on its first billing and refuses every
 * later charge in another, which is why a member who once paid in euro cannot buy
 * anything in kronor. Moving them cancels and recreates their live subscriptions,
 * changing the amount they are billed — so this dialog states both figures and the
 * unchanged renewal date, and does nothing until they agree.
 */
export function CurrencySwitchDialog({
  existingCurrency,
  requestedCurrency,
  onSwitched,
  onDismiss,
}: CurrencySwitchDialogProps) {
  const texts = useLocalizedTexts();
  const [phase, setPhase] = useState<Phase>("loading");
  const [preview, setPreview] = useState<CurrencyMigrationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setPhase("loading");
    setError(null);

    const result = await fetchCurrencyMigrationPreview(controller.signal);
    if (controller.signal.aborted) return;

    if (!result.ok) {
      setError(result.error);
      setPhase("error");
      return;
    }

    setPreview(result.data);
    if (result.data.blockers.length > 0) {
      setPhase("blocked");
    } else if (
      !result.data.eligible &&
      result.data.ineligibilityReason === "no_conflict"
    ) {
      // The conflict resolved itself between the 409 and this call — their checkout
      // will work now, so send them straight back to it rather than showing an
      // empty dialog.
      onSwitched();
    } else if (!result.data.eligible || !result.data.canExecute) {
      // Ineligible for any OTHER reason — the kill switch is on, or we couldn't
      // resolve their customer. Retrying the checkout would 409 again and reopen
      // this dialog forever, so say so and let them choose another route.
      setPhase("unavailable");
    } else {
      setPhase("offer");
    }
  }, [onSwitched]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const confirm = useCallback(async () => {
    if (!preview?.planHash) return;
    setPhase("switching");
    setError(null);

    const result = await executeCurrencyMigration(preview.planHash);
    if (result.ok) {
      setPhase("switched");
      return;
    }

    switch (result.error.kind) {
      case "nothing_to_migrate":
      case "already_migrated":
        // Already done — by another tab, or because they stopped conflicting.
        // Either way the checkout can proceed.
        setPhase("switched");
        return;
      case "preview_changed":
      case "already_in_progress":
        // Their subscriptions moved. Re-draw with the real figures rather than
        // letting them confirm numbers that are no longer true.
        await load();
        return;
      case "blocked":
        await load();
        return;
      default:
        setError(result.error.message);
        setPhase("error");
    }
  }, [preview, load]);

  const sourceCurrency = (
    preview?.sourceCurrency ??
    existingCurrency ??
    ""
  ).toUpperCase();
  const targetCurrency = (
    preview?.targetCurrency ??
    requestedCurrency ??
    ""
  ).toUpperCase();

  return (
    <ModalShell
      isOpen
      labelledBy="currency-switch-title"
      // Never dismissable mid-switch: subscriptions are being cancelled and
      // recreated, and a member who clicks away here cannot tell what happened.
      dismissible={phase !== "switching"}
      onDismiss={onDismiss}
      panelClassName={styles.panel}
    >
      {phase === "loading" && (
        <div className={styles.body}>
          <p className={styles.subtitle}>
            {t(
              texts,
              "Tracker.Checkout.CurrencySwitch.Loading",
              "Checking your subscriptions…"
            )}
          </p>
        </div>
      )}

      {(phase === "offer" || phase === "switching") && preview && (
        <OfferState
          preview={preview}
          sourceCurrency={sourceCurrency}
          targetCurrency={targetCurrency}
          isSwitching={phase === "switching"}
          onConfirm={confirm}
          onDismiss={onDismiss}
        />
      )}

      {phase === "switched" && (
        <SwitchedState
          targetCurrency={targetCurrency}
          onContinue={onSwitched}
        />
      )}

      {phase === "blocked" && preview && (
        <BlockedState
          blockers={preview.blockers}
          billingCurrency={preview.sourceCurrency ?? existingCurrency}
          onDismiss={onDismiss}
        />
      )}

      {phase === "unavailable" && (
        <UnavailableState
          billingCurrency={preview?.sourceCurrency ?? existingCurrency}
          onDismiss={onDismiss}
        />
      )}

      {phase === "error" && (
        <div>
          <div className={styles.header}>
            <h2 className={styles.title} id="currency-switch-title">
              {t(
                texts,
                "Tracker.Checkout.CurrencySwitch.ErrorTitle",
                "We couldn't complete the switch"
              )}
            </h2>
            <p className={styles.subtitle}>
              {error ??
                t(
                  texts,
                  "Tracker.Checkout.CurrencySwitch.ErrorBody",
                  "Nothing has been changed. Please try again, or contact support."
                )}
            </p>
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.button} onClick={onDismiss}>
              {t(texts, "Tracker.Checkout.CurrencySwitch.Close", "Close")}
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.primary}`}
              onClick={load}
            >
              {t(texts, "Tracker.Checkout.CurrencySwitch.Retry", "Try again")}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

/** State 1 — the offer, with every figure the member is agreeing to. */
function OfferState({
  preview,
  sourceCurrency,
  targetCurrency,
  isSwitching,
  onConfirm,
  onDismiss,
}: {
  preview: CurrencyMigrationPreview;
  sourceCurrency: string;
  targetCurrency: string;
  isSwitching: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const texts = useLocalizedTexts();
  const hasSubscriptions = preview.items.length > 0;

  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title} id="currency-switch-title">
          {fillTemplate(
            t(
              texts,
              "Tracker.Checkout.CurrencySwitch.Title",
              "Your subscription is billed in {currency}"
            ),
            { currency: sourceCurrency }
          )}
        </h2>
        <p className={styles.subtitle}>
          {hasSubscriptions
            ? fillTemplate(
                t(
                  texts,
                  "Tracker.Checkout.CurrencySwitch.Intro",
                  "To pay in {targetCurrency}, we'll move your existing subscription over first. Your payment date doesn't change."
                ),
                { targetCurrency }
              )
            : fillTemplate(
                t(
                  texts,
                  "Tracker.Checkout.CurrencySwitch.IntroNoSubscriptions",
                  "To pay in {targetCurrency}, we just need to switch your billing over. Nothing else changes."
                ),
                { targetCurrency }
              )}
        </p>
      </div>

      <div className={styles.body}>
        {hasSubscriptions && (
          <ul className={styles.swap}>
            {preview.items.map((item) => (
              <SwapRow key={item.subscriptionId} item={item} />
            ))}
          </ul>
        )}

        <ul className={styles.points}>
          <li>
            <strong>
              {t(
                texts,
                "Tracker.Checkout.CurrencySwitch.NoChargeToday",
                "You're not charged today."
              )}
            </strong>{" "}
            {t(
              texts,
              "Tracker.Checkout.CurrencySwitch.NoChargeTodayBody",
              "You keep everything you've already paid for."
            )}
          </li>
          {hasSubscriptions && (
            <li>
              {t(
                texts,
                "Tracker.Checkout.CurrencySwitch.SameDate",
                "Your next payment is on the same date as before, in the new currency."
              )}
            </li>
          )}
          <li>
            {t(
              texts,
              "Tracker.Checkout.CurrencySwitch.ReenterCard",
              "You'll enter your card again at checkout — cards don't carry over between currencies."
            )}
          </li>
        </ul>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={`${styles.button} ${styles.ghost}`}
          onClick={onDismiss}
          disabled={isSwitching}
        >
          {t(texts, "Tracker.Checkout.CurrencySwitch.NotNow", "Not now")}
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.primary}`}
          onClick={onConfirm}
          disabled={isSwitching}
        >
          {isSwitching
            ? t(
                texts,
                "Tracker.Checkout.CurrencySwitch.Switching",
                "Switching…"
              )
            : fillTemplate(
                t(
                  texts,
                  "Tracker.Checkout.CurrencySwitch.Confirm",
                  "Switch to {targetCurrency} and continue"
                ),
                { targetCurrency }
              )}
        </button>
      </div>

      <p className={styles.fine}>
        {t(
          texts,
          "Tracker.Checkout.CurrencySwitch.Fine",
          "Switching applies to all your subscriptions. Prices in the new currency are our local list prices, not a conversion."
        )}
      </p>
    </div>
  );
}

/** One subscription: struck-through old price, new price, unchanged renewal date. */
function SwapRow({ item }: { item: CurrencyMigrationItem }) {
  const texts = useLocalizedTexts();
  const oldPrice = formatMinorAmount(
    item.currentAmountMinor,
    item.currentCurrency
  );
  const newPrice = formatMinorAmount(item.newAmountMinor, item.newCurrency);
  const renews = formatRenewalDate(item.renewsAt);

  return (
    <li className={styles.swapRow}>
      <div className={styles.swapName}>
        <strong>
          {item.productName ??
            item.sku ??
            t(
              texts,
              "Tracker.Checkout.CurrencySwitch.SubscriptionFallback",
              "Subscription"
            )}
        </strong>
        {renews && (
          <span>
            {t(texts, "Tracker.Checkout.CurrencySwitch.RenewsOn", "Renews")}{" "}
            {renews}
          </span>
        )}
      </div>
      <div className={styles.swapPrices}>
        {oldPrice && <s className={styles.oldPrice}>{oldPrice}</s>}
        {newPrice && <span className={styles.newPrice}>{newPrice}</span>}
        <span className={styles.interval}>{formatInterval(item)}</span>
      </div>
    </li>
  );
}

/** State 2 — switched, nothing charged, back to checkout. */
function SwitchedState({
  targetCurrency,
  onContinue,
}: {
  targetCurrency: string;
  onContinue: () => void;
}) {
  const texts = useLocalizedTexts();
  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title} id="currency-switch-title">
          {fillTemplate(
            t(
              texts,
              "Tracker.Checkout.CurrencySwitch.DoneTitle",
              "You're set up in {targetCurrency}"
            ),
            { targetCurrency }
          )}
        </h2>
        <p className={styles.subtitle}>
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.DoneBody",
            "Nothing was charged, and your cover runs on unchanged. Taking you back to checkout."
          )}
        </p>
      </div>
      <div className={styles.footer}>
        <button
          type="button"
          className={`${styles.button} ${styles.primary}`}
          onClick={onContinue}
        >
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.Continue",
            "Continue to payment"
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * The switch is not on offer at all — the kill switch is off, or we could not
 * resolve their Stripe customer. Deliberately NOT a bounce back to checkout: that
 * would 409 again and reopen this dialog in a loop.
 */
function UnavailableState({
  billingCurrency,
  onDismiss,
}: {
  billingCurrency: string | null;
  onDismiss: () => void;
}) {
  const texts = useLocalizedTexts();
  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title} id="currency-switch-title">
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.UnavailableTitle",
            "We can't switch your currency right now"
          )}
        </h2>
        <p className={styles.subtitle}>
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.UnavailableBody",
            "Nothing has been changed. You can shop in the currency your account is billed in instead — that starts a new basket — or contact support and we'll sort it out with you."
          )}
        </p>
      </div>
      <div className={styles.footer}>
        <button type="button" className={styles.button} onClick={onDismiss}>
          {t(texts, "Tracker.Checkout.CurrencySwitch.Close", "Close")}
        </button>
        <ChangeMarketAction billingCurrency={billingCurrency} isPrimary />
      </div>
    </div>
  );
}

/** State 3 — refused, naming what stopped it. Nothing was changed. */
function BlockedState({
  blockers,
  billingCurrency,
  onDismiss,
}: {
  blockers: CurrencyMigrationBlocker[];
  billingCurrency: string | null;
  onDismiss: () => void;
}) {
  const texts = useLocalizedTexts();

  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title} id="currency-switch-title">
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.BlockedTitle",
            "We can't switch this one for you"
          )}
        </h2>
        <p className={styles.subtitle}>
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.BlockedBody",
            "One of your subscriptions can't be moved automatically, so we can't bill you in this currency. You can shop in the currency your account is billed in instead — that starts a new basket."
          )}
        </p>
      </div>

      <div className={styles.body}>
        <ul className={styles.swap}>
          {blockers.map((blocker) => (
            <li
              key={`${blocker.type}-${blocker.reason}-${blocker.description ?? ""}`}
              className={styles.swapRow}
            >
              <div className={styles.swapName}>
                <strong>
                  {blocker.description ??
                    t(
                      texts,
                      "Tracker.Checkout.CurrencySwitch.SubscriptionFallback",
                      "Subscription"
                    )}
                </strong>
                <span>{blockerReason(texts, blocker)}</span>
              </div>
            </li>
          ))}
        </ul>

        <p className={styles.reassure}>
          {t(
            texts,
            "Tracker.Checkout.CurrencySwitch.BlockedReassure",
            "Nothing has been changed or cancelled. Your subscriptions are exactly as they were."
          )}
        </p>
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.button} onClick={onDismiss}>
          {t(texts, "Tracker.Checkout.CurrencySwitch.Close", "Close")}
        </button>
        <ChangeMarketAction billingCurrency={billingCurrency} isPrimary />
      </div>
    </div>
  );
}

function blockerReason(
  texts: Record<string, string>,
  blocker: CurrencyMigrationBlocker
): string {
  switch (blocker.reason) {
    case "externally_billed":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.ExternallyBilled",
        "Billed through an app store"
      );
    case "not_cleanly_live":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.NotCleanlyLive",
        "Has a payment we're still processing"
      );
    case "no_price_in_target_currency":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.NoPrice",
        "Not sold in this currency"
      );
    case "has_schedule":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.HasSchedule",
        "Has a scheduled change coming up"
      );
    case "open_quote":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.OpenQuote",
        "You have an open quote"
      );
    case "pending_invoice_item":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.PendingCharge",
        "You have a charge waiting to be invoiced"
      );
    case "non_zero_balance":
      // Stripe holds account credit per currency, so it cannot come with them.
      // Named plainly because it is the member's own money.
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.AccountCredit",
        "You have account credit that can't move to another currency"
      );
    case "legacy_account_subscription":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.LegacySubscription",
        "You have an older subscription that has to be moved by support"
      );
    case "legacy_lookup_failed":
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.LegacyLookupFailed",
        "We couldn't check all of your subscriptions just now — please try again later"
      );
    default:
      return t(
        texts,
        "Tracker.Checkout.CurrencySwitch.Blocked.Generic",
        "Needs to be moved by support"
      );
  }
}

/** Locale-formatted renewal date; null when the backend had none to give. */
function formatRenewalDate(iso: string | null): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** "/ year", "/ 3 months" — kept minimal so it reads next to the price. */
function formatInterval(item: CurrencyMigrationItem): string {
  if (!item.interval) return "";
  const count = item.intervalCount > 1 ? `${item.intervalCount} ` : "";
  const unit = item.intervalCount > 1 ? `${item.interval}s` : item.interval;
  return `/ ${count}${unit}`;
}
